// ContactManagementAdmin.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Building,
  User,
  Edit,
  Trash2,
  Search,
  Filter,
  ArrowLeft,
  Save,
  Globe,
  Users,
  Briefcase,
  Eye,
  X
} from 'lucide-react';

import '../css/Contactdetails.css';

const Contactdetails = () => {
  const [contacts, setContacts] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'add', 'edit', 'view'
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    departmentName: '',
    departmentHead: '',
    email: '',
    phone: '',
    alternatePhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    //country: '',
    //website: '',
    //workingHours: '',
    description: '',
    //teamSize: '',
    //officeNumber: ''
  });

  const [errors, setErrors] = useState({});

  // Load contacts
  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setIsLoading(true);
    // Simulate API call - replace with actual backend call
    setTimeout(() => {
      const sampleContacts = [
        {
          id: 1,
          departmentName: 'Human Resources',
          departmentHead: 'Sarah Johnson',
          email: 'hr@company.com',
          phone: '+1 (555) 123-4567',
          alternatePhone: '+1 (555) 123-4568',
          address: '123 Business Ave, Suite 400',
          city: 'New York',
          state: 'NY',
          zipCode: '10001',
          //country: 'United States',
          //website: 'www.company.com/hr',
          //workingHours: 'Mon-Fri: 9:00 AM - 5:00 PM',
          description: 'Handles employee relations, recruitment, and benefits',
          //teamSize: '15',
          //officeNumber: '401',
          createdAt: '2025-01-15T10:30:00Z',
          updatedAt: '2025-01-20T14:20:00Z'
        },
        {
          id: 2,
          departmentName: 'Information Technology',
          departmentHead: 'Michael Chen',
          email: 'it@company.com',
          phone: '+1 (555) 234-5678',
          alternatePhone: '+1 (555) 234-5679',
          address: '123 Business Ave, Suite 500',
          city: 'New York',
          state: 'NY',
          zipCode: '10001',
          //country: 'United States',
          //website: 'www.company.com/it',
          //workingHours: '24/7 Support Available',
          description: 'Manages company technology infrastructure and support',
          //teamSize: '25',
          //officeNumber: '501',
          createdAt: '2025-01-10T09:15:00Z',
          updatedAt: '2025-01-18T11:30:00Z'
        },
        {
          id: 3,
          departmentName: 'Marketing',
          departmentHead: 'Emily Rodriguez',
          email: 'marketing@company.com',
          phone: '+1 (555) 345-6789',
          alternatePhone: '+1 (555) 345-6790',
          address: '123 Business Ave, Suite 300',
          city: 'New York',
          state: 'NY',
          zipCode: '10001',
          //country: 'United States',
          //website: 'www.company.com/marketing',
          //workingHours: 'Mon-Fri: 8:00 AM - 6:00 PM',
          description: 'Develops marketing strategies and campaigns',
          //teamSize: '20',
          //officeNumber: '301',
          createdAt: '2025-01-12T13:45:00Z',
          updatedAt: '2025-01-19T16:10:00Z'
        }
      ];
      setContacts(sampleContacts);
      setIsLoading(false);
    }, 1000);
  };

  const departments = ['all', ...new Set(contacts.map(contact => contact.departmentName))];

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.departmentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.departmentHead.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.phone.includes(searchTerm);
    const matchesDepartment = filterDepartment === 'all' || contact.departmentName === filterDepartment;
    return matchesSearch && matchesDepartment;
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.departmentName.trim()) newErrors.departmentName = 'Department name is required';
    if (!formData.departmentHead.trim()) newErrors.departmentHead = 'Department head is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert('Please fill in all required fields correctly');
      return;
    }

    setIsLoading(true);

    try {
      const contactData = {
        id: viewMode === 'edit' ? selectedContact.id : Date.now(),
        ...formData,
        createdAt: viewMode === 'edit' ? selectedContact.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (viewMode === 'edit') {
        // Update existing contact
        setContacts(prev => prev.map(c => c.id === selectedContact.id ? contactData : c));
        alert('Contact updated successfully!');
      } else {
        // Add new contact
        setContacts(prev => [contactData, ...prev]);
        alert('Contact added successfully!');
      }

      resetForm();
      setViewMode('list');
    } catch (error) {
      console.error('Failed to save contact:', error);
      alert('Failed to save contact. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      departmentName: '',
      departmentHead: '',
      email: '',
      phone: '',
      alternatePhone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      //country: '',
      //website: '',
      //workingHours: '',
      description: '',
      //teamSize: '',
      //officeNumber: ''
    });
    setErrors({});
    setSelectedContact(null);
  };

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setFormData({
      departmentName: contact.departmentName,
      departmentHead: contact.departmentHead,
      email: contact.email,
      phone: contact.phone,
      alternatePhone: contact.alternatePhone,
      address: contact.address,
      city: contact.city,
      state: contact.state,
      zipCode: contact.zipCode,
      //country: contact.country,
      //website: contact.website,
      //workingHours: contact.workingHours,
      description: contact.description,
      //teamSize: contact.teamSize,
      //officeNumber: contact.officeNumber
    });
    setViewMode('edit');
  };

  const handleDelete = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      setIsLoading(true);
      try {
        setContacts(prev => prev.filter(c => c.id !== contactId));
        alert('Contact deleted successfully!');
      } catch (error) {
        console.error('Delete failed:', error);
        alert('Delete failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Styles
  // const styles = {
  //   container: {
  //     minHeight: '100vh',
  //     backgroundColor: '#f9fafb',
  //     padding: '24px'
  //   },
  //   header: {
  //     display: 'flex',
  //     flexDirection: 'column',
  //     justifyContent: 'space-between',
  //     alignItems: 'flex-start',
  //     gap: '16px',
  //     marginBottom: '24px'
  //   },
  //   title: {
  //     fontSize: '30px',
  //     fontWeight: '700',
  //     color: '#111827'
  //   },
  //   subtitle: {
  //     color: '#6b7280',
  //     marginTop: '4px'
  //   },
  //   btnPrimary: {
  //     display: 'flex',
  //     alignItems: 'center',
  //     gap: '8px',
  //     backgroundColor: '#2563eb',
  //     color: 'white',
  //     padding: '12px 24px',
  //     borderRadius: '8px',
  //     border: 'none',
  //     fontWeight: '600',
  //     cursor: 'pointer',
  //     transition: 'background-color 0.2s'
  //   },
  //   btnSecondary: {
  //     padding: '8px',
  //     color: '#6b7280',
  //     background: 'none',
  //     border: 'none',
  //     cursor: 'pointer',
  //     transition: 'color 0.2s'
  //   },
  //   searchBar: {
  //     display: 'flex',
  //     flexDirection: 'column',
  //     gap: '16px',
  //     background: 'white',
  //     padding: '16px',
  //     borderRadius: '8px',
  //     boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  //     marginBottom: '24px'
  //   },
  //   searchContainer: {
  //     position: 'relative',
  //     flex: '1'
  //   },
  //   searchIcon: {
  //     position: 'absolute',
  //     left: '12px',
  //     top: '50%',
  //     transform: 'translateY(-50%)',
  //     color: '#9ca3af',
  //     width: '20px',
  //     height: '20px'
  //   },
  //   searchInput: {
  //     width: '100%',
  //     padding: '8px 8px 8px 40px',
  //     border: '1px solid #d1d5db',
  //     borderRadius: '8px',
  //     outline: 'none',
  //     transition: 'all 0.2s'
  //   },
  //   filterContainer: {
  //     display: 'flex',
  //     alignItems: 'center',
  //     gap: '8px'
  //   },
  //   filterSelect: {
  //     padding: '8px 12px',
  //     border: '1px solid #d1d5db',
  //     borderRadius: '8px',
  //     outline: 'none',
  //     transition: 'all 0.2s'
  //   },
  //   grid: {
  //     display: 'grid',
  //     gridTemplateColumns: '1fr',
  //     gap: '24px'
  //   },
  //   card: {
  //     background: 'white',
  //     borderRadius: '8px',
  //     boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  //     overflow: 'hidden',
  //     transition: 'box-shadow 0.2s'
  //   },
  //   cardHeader: {
  //     background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  //     color: 'white',
  //     padding: '20px',
  //     display: 'flex',
  //     justifyContent: 'space-between',
  //     alignItems: 'flex-start'
  //   },
  //   cardTitle: {
  //     fontSize: '20px',
  //     fontWeight: '700',
  //     marginBottom: '4px'
  //   },
  //   cardSubtitle: {
  //     fontSize: '14px',
  //     opacity: '0.9'
  //   },
  //   cardActions: {
  //     display: 'flex',
  //     gap: '8px'
  //   },
  //   btnIcon: {
  //     padding: '8px',
  //     borderRadius: '50%',
  //     border: 'none',
  //     cursor: 'pointer',
  //     transition: 'all 0.2s',
  //     backgroundColor: 'rgba(255, 255, 255, 0.2)',
  //     color: 'white'
  //   },
  //   cardBody: {
  //     padding: '20px'
  //   },
  //   infoGrid: {
  //     display: 'grid',
  //     gridTemplateColumns: '1fr',
  //     gap: '16px'
  //   },
  //   infoItem: {
  //     display: 'flex',
  //     alignItems: 'flex-start',
  //     gap: '12px'
  //   },
  //   infoIcon: {
  //     width: '20px',
  //     height: '20px',
  //     color: '#2563eb',
  //     flexShrink: '0',
  //     marginTop: '2px'
  //   },
  //   infoContent: {
  //     flex: '1'
  //   },
  //   infoLabel: {
  //     fontSize: '12px',
  //     fontWeight: '600',
  //     color: '#6b7280',
  //     textTransform: 'uppercase',
  //     letterSpacing: '0.5px',
  //     marginBottom: '4px'
  //   },
  //   infoValue: {
  //     fontSize: '14px',
  //     color: '#111827',
  //     wordBreak: 'break-word'
  //   },
  //   formContainer: {
  //     maxWidth: '800px',
  //     margin: '0 auto'
  //   },
  //   formHeader: {
  //     display: 'flex',
  //     alignItems: 'center',
  //     gap: '16px',
  //     marginBottom: '24px'
  //   },
  //   formCard: {
  //     background: 'white',
  //     borderRadius: '8px',
  //     boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  //     padding: '24px'
  //   },
  //   formSection: {
  //     marginBottom: '32px'
  //   },
  //   sectionTitle: {
  //     fontSize: '18px',
  //     fontWeight: '700',
  //     color: '#111827',
  //     marginBottom: '16px',
  //     paddingBottom: '8px',
  //     borderBottom: '2px solid #e5e7eb'
  //   },
  //   formGrid: {
  //     display: 'grid',
  //     gridTemplateColumns: '1fr',
  //     gap: '16px'
  //   },
  //   formGroup: {
  //     marginBottom: '0'
  //   },
  //   label: {
  //     display: 'block',
  //     fontSize: '14px',
  //     fontWeight: '600',
  //     color: '#374151',
  //     marginBottom: '6px'
  //   },
  //   required: {
  //     color: '#ef4444',
  //     marginLeft: '4px'
  //   },
  //   input: {
  //     width: '100%',
  //     padding: '10px 12px',
  //     border: '1px solid #d1d5db',
  //     borderRadius: '6px',
  //     outline: 'none',
  //     transition: 'all 0.2s',
  //     fontSize: '14px'
  //   },
  //   inputError: {
  //     borderColor: '#ef4444'
  //   },
  //   textarea: {
  //     width: '100%',
  //     padding: '10px 12px',
  //     border: '1px solid #d1d5db',
  //     borderRadius: '6px',
  //     outline: 'none',
  //     transition: 'all 0.2s',
  //     resize: 'vertical',
  //     minHeight: '80px',
  //     fontSize: '14px'
  //   },
  //   errorText: {
  //     fontSize: '12px',
  //     color: '#ef4444',
  //     marginTop: '4px'
  //   },
  //   formActions: {
  //     display: 'flex',
  //     justifyContent: 'flex-end',
  //     gap: '12px',
  //     paddingTop: '24px',
  //     borderTop: '1px solid #e5e7eb',
  //     marginTop: '24px'
  //   },
  //   btnCancel: {
  //     padding: '10px 20px',
  //     border: '1px solid #d1d5db',
  //     borderRadius: '6px',
  //     background: 'white',
  //     color: '#374151',
  //     fontWeight: '600',
  //     cursor: 'pointer',
  //     transition: 'all 0.2s'
  //   },
  //   emptyState: {
  //     textAlign: 'center',
  //     padding: '48px 0'
  //   },
  //   emptyIcon: {
  //     width: '64px',
  //     height: '64px',
  //     color: '#9ca3af',
  //     margin: '0 auto 16px'
  //   },
  //   emptyText: {
  //     color: '#6b7280',
  //     fontSize: '18px'
  //   },
  //   loadingOverlay: {
  //     position: 'fixed',
  //     top: '0',
  //     left: '0',
  //     right: '0',
  //     bottom: '0',
  //     background: 'rgba(0, 0, 0, 0.5)',
  //     display: 'flex',
  //     alignItems: 'center',
  //     justifyContent: 'center',
  //     zIndex: '50'
  //   },
  //   loadingContent: {
  //     background: 'white',
  //     padding: '24px',
  //     borderRadius: '8px',
  //     display: 'flex',
  //     alignItems: 'center',
  //     gap: '12px'
  //   },
  //   spinner: {
  //     width: '24px',
  //     height: '24px',
  //     border: '2px solid #3b82f6',
  //     borderTopColor: 'transparent',
  //     borderRadius: '50%',
  //     animation: 'spin 1s linear infinite'
  //   },
  //   detailCard: {
  //     background: 'white',
  //     borderRadius: '8px',
  //     boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  //     overflow: 'hidden',
  //     maxWidth: '800px',
  //     margin: '0 auto'
  //   },
  //   detailHeader: {
  //     background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  //     color: 'white',
  //     padding: '32px 24px'
  //   },
  //   detailTitle: {
  //     fontSize: '28px',
  //     fontWeight: '700',
  //     marginBottom: '8px'
  //   },
  //   detailSubtitle: {
  //     fontSize: '16px',
  //     opacity: '0.9'
  //   },
  //   detailBody: {
  //     padding: '24px'
  //   },
  //   detailGrid: {
  //     display: 'grid',
  //     gridTemplateColumns: '1fr',
  //     gap: '24px'
  //   },
  //   detailSection: {
  //     paddingBottom: '24px',
  //     borderBottom: '1px solid #e5e7eb'
  //   },
  //   detailSectionTitle: {
  //     fontSize: '16px',
  //     fontWeight: '700',
  //     color: '#111827',
  //     marginBottom: '16px',
  //     display: 'flex',
  //     alignItems: 'center',
  //     gap: '8px'
  //   }
  // };

  // List View
  const ListView = () => (
    <div className='container'>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .btn-primary:hover {
            background-color: #1d4ed8 !important;
          }
          .btn-secondary:hover {
            color: #374151 !important;
          }
          .card:hover {
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
          }
          .btn-icon:hover {
            background-color: rgba(255, 255, 255, 0.3) !important;
          }
          .btn-cancel:hover {
            background-color: #f3f4f6 !important;
          }
          .input:focus, .textarea:focus, .select:focus {
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
          }
          .input-error {
            border-color: #ef4444 !important;
          }
          @media (min-width: 768px) {
            .header { flex-direction: row !important; align-items: center !important; }
            .search-bar { flex-direction: row !important; }
            .info-grid { grid-template-columns: repeat(2, 1fr) !important; }
            .form-grid { grid-template-columns: repeat(2, 1fr) !important; }
            .detail-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
          @media (min-width: 1024px) {
            .grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
        `}
      </style>

      {/* Header */}
      <div 
      //style={styles.header} 
      className="header">
        <div>
          <h1 className='contitle'>Department Contacts</h1>
          <p className='consubtitle'>Manage all department contact information</p>
        </div>
        <button
          onClick={() => setViewMode('add')}
          //style={styles.btnPrimary}
          className="btnPrimary"
        >
          <Plus size={20} />
          Add Contact
        </button>
      </div>

      {/* Search and Filter */}
      <div 
      //style={styles.searchBar} 
      className="searchBar">
        <div className='searchContainer'>
          <Search className='searchIcon'/>
          <input
            type="text"
            placeholder="Search departments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            //style={styles.searchInput}
            className="searchInput"
          />
        </div>
        <div className='filterContainer'>
          <Filter size={20} color="#6b7280" />
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            //style={styles.filterSelect}
            className="filterSelect"
          >
            {departments.map(dept => (
              <option key={dept} value={dept}>
                {dept === 'all' ? 'All Departments' : dept}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Contacts Grid */}
      <div 
      //style={styles.grid} 
      className="grid">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} style={{ backgroundColor: '#e5e7eb', borderRadius: '8px', height: '300px' }}></div>
          ))
        ) : filteredContacts.length === 0 ? (
          <div className='emptyState'>
            <Building className='emptyIcon' />
            <p className='emptyText'>No contacts found</p>
          </div>
        ) : (
          filteredContacts.map(contact => (
            <div key={contact.id} 
            //style={styles.card} 
            className="card">
              <div className='cardHeader'>
                <div>
                  <h3 className='cardTitle'>{contact.departmentName}</h3>
                  <p className='cardSubtitle'>
                    <User size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                    {contact.departmentHead}
                  </p>
                </div>
                <div className='cardActions'>
                  <button
                    onClick={() => {
                      setSelectedContact(contact);
                      setViewMode('view');
                    }}
                    //style={styles.btnIcon}
                    className="btnIcon"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => handleEdit(contact)}
                    //style={styles.btnIcon}
                    className="btnIcon"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(contact.id)}
                    //style={styles.btnIcon}
                    className="btnIcon"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className='cardBody'>
                <div className="infoGrid">
                  <div className='infoItem'>
                    <Mail className='infoIcon' />
                    <div className='infoContent'>
                      <div className='infoLabel'>Email</div>
                      <div className='infoValue'>{contact.email}</div>
                    </div>
                  </div>
                  <div className='infoItem'>
                    <Phone className='infoIcon' />
                    <div className='infoContent'>
                      <div className='infoLabel'>Phone</div>
                      <div className='infoValue'>{contact.phone}</div>
                    </div>
                  </div>
                  <div className='infoItem' >
                    <MapPin className='infoIcon' />
                    <div className='infoContent'>
                      <div className='infoLabel'>Location</div>
                      <div className='infoValue'>{contact.city}, {contact.state}</div>
                    </div>
                  </div>
                  <div className='infoItem'>
                    <Users className='infoIcon' />
                    <div className='infoContent'>
                      <div className='infoLabel'>Team Size</div>
                      <div className='infoValue'>{contact.teamSize} members</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Form View (Add/Edit)
  const FormView = () => (
    <div className='container'>
      <div className='formContainer'>
        <div className='formHeader'>
          <button
            onClick={() => {
              resetForm();
              setViewMode('list');
            }}
            //style={styles.btnSecondary}
            className="btnSecondary"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className='contitle'>
            {viewMode === 'edit' ? 'Edit Contact' : 'Add New Contact'}
          </h1>
        </div>

        <div className='formCard'>
          <form onSubmit={handleSubmit}>
            {/* Department Information */}
            <div className='formSection'>
              <h3 className='sectionTitle'>
                <Building size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                Department Information
              </h3>
              <div className='formGrid'>
                <div className='formGroup'>
                  <label className='label'>
                    Department Name<span className='required'>*</span>
                  </label>
                  <input
                    type="text"
                    name="departmentName"
                    value={formData.departmentName}
                    onChange={handleInputChange}
                    placeholder="e.g., Human Resources"
                    //style={{ ...(errors.departmentName ? styles.inputError : {})}}
                    className={`input ${errors.departmentName ? 'inputError' : ''}`}
                  />
                  {errors.departmentName && <div className='errorText'>{errors.departmentName}</div>}
                </div>
                <div className='formGroup'>
                  <label className='label'>
                    Department Head<span className='required'>*</span>
                  </label>
                  <input
                    type="text"
                    name="departmentHead"
                    value={formData.departmentHead}
                    onChange={handleInputChange}
                    placeholder="Full name"
                    //style={{...styles.input, ...(errors.departmentHead ? styles.inputError : {})}}
                    className={`input ${errors.departmentHead ? 'inputError' : ''}`}
                  />
                  {errors.departmentHead && <div className='errorText'>{errors.departmentHead}</div>}
                </div>
                <div className='formGroup'>
                  <label className='label'>
                    Team Size
                  </label>
                  <input
                    type="number"
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={handleInputChange}
                    placeholder="Number of team members"
                    //style={styles.input}
                    className="input"
                  />
                </div>
                {/* <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Office Number
                  </label>
                  <input
                    type="text"
                    name="officeNumber"
                    value={formData.officeNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., 401"
                    style={styles.input}
                    className="input"
                  />
                </div> */}
              </div>
              <div style={{marginTop: '16px'}}>
                <div className='formGroup'>
                  <label className='label'>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the department"
                    //style={styles.textarea}
                    className="textarea"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className='formSection'>
              <h3 className='sectionTitle'>
                <Phone size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                Contact Information
              </h3>
              <div className="formGrid">
                <div className='formGroup'>
                  <label className='label'>
                    Email<span className='required'>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="department@company.com"
                    //style={{ ...(errors.email ? styles.inputError : {})}}
                    className={`input ${errors.email ? 'inputError' : ''}`}
                  />
                  {errors.email && <div className='errorText'>{errors.email}</div>}
                </div>
                <div className='formGroup'>
                  <label className='label'>
                    Phone Number<span className='required'>*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    //style={{...styles.input, ...(errors.phone ? styles.inputError : {})}}
                    className={`input ${errors.phone ? 'inputError' : ''}`}
                  />
                  {errors.phone && <div className='errorText'>{errors.phone}</div>}
                </div>
                <div className='formGroup'>
                  <label className='label'>
                    Alternate Phone
                  </label>
                  <input
                    type="tel"
                    name="alternatePhone"
                    value={formData.alternatePhone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4568"
                    //style={styles.input}
                    className="input"
                  />
                </div>
                {/* <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="www.company.com/department"
                    style={styles.input}
                    className="input"
                  />
                </div> */}
              </div>
            </div>

            {/* Address Information */}
            <div className='formSection'>
              <h3 className='sectionTitle'>
                <MapPin size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                Address Information
              </h3>
              <div style={{marginBottom: '16px'}}>
                <div className='formGroup'>
                  <label className='label'>
                    Street Address<span className='required'>*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Business Ave, Suite 400"
                    //style={{...styles.input, ...(errors.address ? styles.inputError : {})}}
                    className={`input ${errors.address ? 'inputError' : ''}`}
                  />
                  {errors.address && <div className='errorText'>{errors.address}</div>}
                </div>
              </div>
              <div className="formGrid">
                <div className='formGroup'>
                  <label className='label'>
                    City<span className='required'>*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="New York"
                    //style={{...styles.input, ...(errors.city ? styles.inputError : {})}}
                    className={`input ${errors.city ? 'inputError' : ''}`}
                  />
                  {errors.city && <div className='errorText'>{errors.city}</div>}
                </div>
                <div className='formGroup'>
                  <label className='label'>
                    State/Province
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="NY"
                    //style={styles.input}
                    className="input"
                  />
                </div>
                <div className='formGroup'>
                  <label className='label'>
                    ZIP/Postal Code
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    placeholder="10001"
                    //style={styles.input}
                    className="input"
                  />
                </div>
                {/* <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="United States"
                    style={styles.input}
                    className="input"
                  />
                </div> */}
              </div>
            </div>

            {/* Additional Information */}
            {/* <div style={styles.formSection}>
              <h3 style={styles.sectionTitle}>
                <Clock size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                Additional Information
              </h3>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Working Hours
                </label>
                <input
                  type="text"
                  name="workingHours"
                  value={formData.workingHours}
                  onChange={handleInputChange}
                  placeholder="Mon-Fri: 9:00 AM - 5:00 PM"
                  style={styles.input}
                  className="input"
                />
              </div>
            </div> */}

            {/* Form Actions */}
            <div className='formActions'>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setViewMode('list');
                }}
                //style={styles.btnCancel}
                className="btnCancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  //...styles.btnPrimary,
                  backgroundColor: isLoading ? '#9ca3af' : '#2563eb',
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
                className="btnPrimary"
              >
                {isLoading ? (
                  <>
                    <div className='spinner'></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    {viewMode === 'edit' ? 'Update Contact' : 'Add Contact'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // Detail View
  const DetailView = () => (
    <div className='container'>
      <div className='formContainer'>
        <div className='formHeader'>
          <button
            onClick={() => setViewMode('list')}
            //style={styles.btnSecondary}
            className="btnSecondary"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className='contitle'>Contact Details</h1>
        </div>

        {selectedContact && (
          <div className='detailCard'>
            <div className='detailHeader'>
              <h2 className='detailTitle'>{selectedContact.departmentName}</h2>
              <p className='detailSubtitle'>
                <User size={16} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                {selectedContact.departmentHead}
              </p>
            </div>
            <div className='detailBody'>
              <div className="detailGrid">
                {/* Contact Information */}
                <div className='detailSection'>
                  <h3 className='detailSectionTitle'>
                    <Phone size={18} />
                    Contact Information
                  </h3>
                  <div style={{ gap: '12px'}} className='infoGrid'>
                    <div className='infoItem'>
                      <Mail className='infoIcon' />
                      <div className='infoContent'>
                        <div className='infoLabel'>Email</div>
                        <div className='infoValue'>{selectedContact.email}</div>
                      </div>
                    </div>
                    <div className='infoItem'>
                      <Phone className='infoIcon' />
                      <div className='infoContent'>
                        <div className='infoLabel'>Phone</div>
                        <div className='infoValue'>{selectedContact.phone}</div>
                      </div>
                    </div>
                    {selectedContact.alternatePhone && (
                      <div className='infoItem'>
                        <Phone className='infoIcon' />
                        <div infoContent>
                          <div className='infoLabel'>Alternate Phone</div>
                          <div className='infoValue'>{selectedContact.alternatePhone}</div>
                        </div>
                      </div>
                    )}
                    {/* {selectedContact.website && (
                      <div style={styles.infoItem}>
                        <Globe style={styles.infoIcon} />
                        <div style={styles.infoContent}>
                          <div style={styles.infoLabel}>Website</div>
                          <div style={styles.infoValue}>{selectedContact.website}</div>
                        </div>
                      </div>
                    )} */}
                  </div>
                </div>

                {/* Location Information */}
                <div className='detailSection'>
                  <h3 className='detailSectionTitle'>
                    <MapPin size={18} />
                    Location
                  </h3>
                  <div style={{gap: '12px'}} className='infoGrid'>
                    <div className='infoItem'>
                      <MapPin className='infoIcon' />
                      <div className='infoContent'>
                        <div className='infoLabel'>Address</div>
                        <div className='infoValue'>
                          {selectedContact.address}<br />
                          {selectedContact.city}, {selectedContact.state} {selectedContact.zipCode}<br />
                          {selectedContact.country}
                        </div>
                      </div>
                    </div>
                    {/* {selectedContact.officeNumber && (
                      <div style={styles.infoItem}>
                        <Building style={styles.infoIcon} />
                        <div style={styles.infoContent}>
                          <div style={styles.infoLabel}>Office Number</div>
                          <div style={styles.infoValue}>{selectedContact.officeNumber}</div>
                        </div>
                      </div>
                    )} */}
                  </div>
                </div>

                {/* Department Information */}
                <div className='detailSection'>
                  <h3 className='detailSectionTitle'>
                    <Building size={18} />
                    Department Details
                  </h3>
                  <div style={{gap: '12px'}} className='infoGrid'>
                    {/* {selectedContact.teamSize && (
                      <div style={styles.infoItem}>
                        <Users style={styles.infoIcon} />
                        <div style={styles.infoContent}>
                          <div style={styles.infoLabel}>Team Size</div>
                          <div style={styles.infoValue}>{selectedContact.teamSize} members</div>
                        </div> 
                      </div>
                    )}
                    {selectedContact.workingHours && (
                      <div style={styles.infoItem}>
                        <Clock style={styles.infoIcon} />
                        <div style={styles.infoContent}>
                          <div style={styles.infoLabel}>Working Hours</div>
                          <div style={styles.infoValue}>{selectedContact.workingHours}</div>
                        </div>
                      </div>
                    )} */}
                    {selectedContact.description && (
                      <div style={{gridColumn: '1 / -1'}} className='infoItem'>
                        <Briefcase className='infoIcon' />
                        <div className='infoContent'>
                          <div className='infoLabel'>Description</div>
                          <div className='infoValue'>{selectedContact.description}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata */}
                <div className='detailSection'>
                  <h3 className='detailSectionTitle'>
                    <Clock size={18} />
                    Record Information
                  </h3>
                  <div style={{ gap: '12px'}} className='infoGrid'>
                    <div className='infoItem'>
                      <div className='infoContent'>
                        <div className='infoLabel'>Created</div>
                        <div className='infoValue'>{formatDate(selectedContact.createdAt)}</div>
                      </div>
                    </div>
                    <div className='infoItem'>
                      <div className='infoContent'>
                        <div className='infoLabel'>Last Updated</div>
                        <div className='infoValue'>{formatDate(selectedContact.updatedAt)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ borderTop: 'none', paddingTop: '16px', marginTop: '16px'}} className='formActions'>
                <button
                  onClick={() => handleEdit(selectedContact)}
                  //style={styles.btnPrimary}
                  className="btnPrimary"
                >
                  <Edit size={20} />
                  Edit Contact
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {isLoading && (
        <div className='loadingOverlay'>
          <div className='loadingContent'>
            <div className='spinner'></div>
            <span>Processing...</span>
          </div>
        </div>
      )}

      {viewMode === 'list' && <ListView />}
      {(viewMode === 'add' || viewMode === 'edit') && <FormView />}
      {viewMode === 'view' && <DetailView />}
    </div>
  );
};

export default Contactdetails;